/**
 * PassportCover - Portada del pasaporte digital
 * Diseño premium con textura de piel, estampado dorado,
 * marco con filigrana y tipografía serif elegante.
 */

import React from "react";
import { Box, Typography } from "@mui/material";
import { LuPlane } from "react-icons/lu";
import WorldIcon from "../../assets/passport/world.png";
import Leather from "../../assets/passport/leather.png";
const PassportCover = ({ passport, countriesCount, profileHolderName }) => {
	// En "mi pasaporte" el perfil manda; en vista compartida solo viene `passport`
	const userName =
		(profileHolderName != null && String(profileHolderName).trim() !== ""
			? String(profileHolderName).trim()
			: null) ||
		passport?.displayName?.trim() ||
		"Viajero";

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				/* Base: azul marino + textura de cuero */
				background: `linear-gradient(170deg, #0f1a2e 0%, #162240 35%, #1a2a4e 65%, #111d35 100%)`,
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				position: "relative",
				overflow: "hidden",
				fontFamily: "'Playfair Display', serif",
			}}
		>
			{/* ── Imagen de cuero como fondo (sobre el gradiente) ── */}
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					backgroundImage: `url(${Leather})`,
					backgroundSize: "cover",
					backgroundPosition: "center",
					backgroundRepeat: "no-repeat",
					opacity: 0.2,
					pointerEvents: "none",
				}}
			/>

			{/* ══════════════════════════════════════ */}
			{/* ══  TEXTURA DE PIEL (múltiples capas) ══ */}
			{/* ══════════════════════════════════════ */}

			{/* Capa 1: grano fino de piel — patrón de puntos diminutos irregulares */}
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					opacity: 0.12,
					backgroundImage: `
						radial-gradient(circle at 2px 2px, rgba(255,255,255,0.08) 1px, transparent 1px),
						radial-gradient(circle at 5px 7px, rgba(0,0,0,0.12) 0.5px, transparent 0.5px),
						radial-gradient(circle at 9px 3px, rgba(255,255,255,0.06) 0.8px, transparent 0.8px)
					`,
					backgroundSize: "11px 11px, 13px 13px, 7px 9px",
					pointerEvents: "none",
				}}
			/>

			{/* Capa 2: micro-arrugas de piel — líneas horizontales sutiles */}
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					opacity: 0.06,
					backgroundImage: `
						repeating-linear-gradient(
							2deg,
							transparent,
							transparent 3px,
							rgba(255,255,255,0.03) 3px,
							rgba(255,255,255,0.03) 4px
						),
						repeating-linear-gradient(
							178deg,
							transparent,
							transparent 5px,
							rgba(0,0,0,0.05) 5px,
							rgba(0,0,0,0.05) 6px
						)
					`,
					pointerEvents: "none",
				}}
			/>

			{/* Capa 3: textura de poro de cuero — puntos más grandes y suaves */}
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					opacity: 0.04,
					backgroundImage: `
						radial-gradient(ellipse at 30% 20%, rgba(0,0,0,0.3) 0%, transparent 0.8%),
						radial-gradient(ellipse at 70% 40%, rgba(0,0,0,0.25) 0%, transparent 0.6%),
						radial-gradient(ellipse at 20% 60%, rgba(0,0,0,0.2) 0%, transparent 0.7%),
						radial-gradient(ellipse at 80% 80%, rgba(0,0,0,0.3) 0%, transparent 0.5%),
						radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0.15) 0%, transparent 1%)
					`,
					backgroundSize:
						"23px 19px, 17px 21px, 29px 23px, 19px 17px, 31px 27px",
					pointerEvents: "none",
				}}
			/>

			{/* Capa 4: ruido de cuero — patrón de celdas tipo Voronoi fake */}
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					opacity: 0.035,
					backgroundImage: `
						conic-gradient(from 0deg at 35% 25%, rgba(255,255,255,0.1) 0deg, transparent 30deg, transparent 330deg, rgba(255,255,255,0.1) 360deg),
						conic-gradient(from 120deg at 65% 75%, rgba(255,255,255,0.08) 0deg, transparent 25deg, transparent 335deg, rgba(255,255,255,0.08) 360deg),
						conic-gradient(from 240deg at 50% 50%, rgba(0,0,0,0.1) 0deg, transparent 20deg, transparent 340deg, rgba(0,0,0,0.1) 360deg)
					`,
					backgroundSize: "40px 40px, 35px 45px, 50px 38px",
					pointerEvents: "none",
				}}
			/>

			{/* Capa 5: brillo sutil de cuero — reflejo central suave */}
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					background: `
						radial-gradient(ellipse at 50% 38%, rgba(201,168,76,0.04) 0%, transparent 55%),
						radial-gradient(ellipse at 30% 25%, rgba(255,255,255,0.015) 0%, transparent 40%),
						radial-gradient(ellipse at 70% 65%, rgba(0,0,0,0.08) 0%, transparent 50%)
					`,
					pointerEvents: "none",
				}}
			/>

			{/* Capa 6: bordes oscurecidos (efecto cuero curvado en los bordes) */}
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					background: `
						linear-gradient(to right, rgba(0,0,0,0.15) 0%, transparent 8%, transparent 92%, rgba(0,0,0,0.15) 100%),
						linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, transparent 6%, transparent 94%, rgba(0,0,0,0.1) 100%)
					`,
					pointerEvents: "none",
				}}
			/>

			{/* ── Marco exterior dorado ── */}
			<Box
				sx={{
					position: "absolute",
					inset: "14px",
					border: "1.5px solid rgba(201, 168, 76, 0.2)",
					borderRadius: "8px",
					pointerEvents: "none",
					/* Sombra interna sutil para profundidad del marco */
					boxShadow: "inset 0 0 20px rgba(0,0,0,0.15)",
				}}
			/>
			<Box
				sx={{
					position: "absolute",
					inset: "18px",
					border: "0.5px solid rgba(201, 168, 76, 0.1)",
					borderRadius: "6px",
					pointerEvents: "none",
				}}
			/>

			{/* ── Esquinas filigrana ── */}
			{["top-left", "top-right", "bottom-left", "bottom-right"].map(
				(corner) => {
					const isTop = corner.includes("top");
					const isLeft = corner.includes("left");
					return (
						<Box
							key={corner}
							sx={{
								position: "absolute",
								[isTop ? "top" : "bottom"]: 24,
								[isLeft ? "left" : "right"]: 24,
								width: 32,
								height: 32,
								pointerEvents: "none",
							}}
						>
							{/* Línea horizontal */}
							<Box
								sx={{
									position: "absolute",
									[isTop ? "top" : "bottom"]: 0,
									[isLeft ? "left" : "right"]: 0,
									width: 32,
									height: "1px",
									background: `linear-gradient(${
										isLeft ? "90deg" : "270deg"
									}, rgba(201,168,76,0.45), transparent)`,
								}}
							/>
							{/* Línea vertical */}
							<Box
								sx={{
									position: "absolute",
									[isTop ? "top" : "bottom"]: 0,
									[isLeft ? "left" : "right"]: 0,
									width: "1px",
									height: 32,
									background: `linear-gradient(${
										isTop ? "180deg" : "0deg"
									}, rgba(201,168,76,0.45), transparent)`,
								}}
							/>
							{/* Pequeño rombo decorativo */}
							<Box
								sx={{
									position: "absolute",
									[isTop ? "top" : "bottom"]: -3,
									[isLeft ? "left" : "right"]: -3,
									width: 6,
									height: 6,
									transform: "rotate(45deg)",
									backgroundColor: "rgba(201,168,76,0.2)",
								}}
							/>
						</Box>
					);
				}
			)}

			{/* ══════════════════════════════════════ */}
			{/* ══        CONTENIDO CENTRAL          ══ */}
			{/* ══════════════════════════════════════ */}

			{/* Línea decorativa superior */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1,
					mb: 2,
				}}
			>
				<Box
					sx={{
						width: 40,
						height: "1px",
						background:
							"linear-gradient(90deg, transparent, rgba(201,168,76,0.35))",
					}}
				/>
				<Box
					sx={{
						width: 4,
						height: 4,
						transform: "rotate(45deg)",
						backgroundColor: "rgba(201,168,76,0.25)",
					}}
				/>
				<Box
					sx={{
						width: 40,
						height: "1px",
						background:
							"linear-gradient(90deg, rgba(201,168,76,0.35), transparent)",
					}}
				/>
			</Box>

			{/* Subtítulo superior */}
			<Typography
				sx={{
					fontSize: { xs: "0.5rem", sm: "0.55rem" },
					color: "rgba(201, 168, 76, 0.85)",
					textTransform: "uppercase",
					letterSpacing: "0.45em",
					mb: 2,
					fontWeight: 500,
					textShadow: "0 1px 2px rgba(0,0,0,0.4), 0 0 8px rgba(0,0,0,0.2)",
				}}
			>
				Viajes Molones
			</Typography>

			{/* ── Escudo / emblema central ── */}
			<Box
				sx={{
					width: { xs: 100, sm: 115 },
					height: { xs: 100, sm: 115 },
					borderRadius: "50%",
					border: "1.5px solid rgba(201, 168, 76, 0.22)",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					mb: 2,
					position: "relative",
					background:
						"radial-gradient(circle, rgba(201,168,76,0.03) 0%, transparent 70%)",
					/* Efecto repujado en la piel */
					boxShadow: `
						0 1px 2px rgba(0,0,0,0.3),
						inset 0 1px 1px rgba(201,168,76,0.05),
						0 0 30px rgba(201,168,76,0.04)
					`,
				}}
			>
				{/* Anillo interior dashed — como cosido */}
				<Box
					sx={{
						position: "absolute",
						inset: 6,
						borderRadius: "50%",
						border: "1px dashed rgba(201, 168, 76, 0.12)",
					}}
				/>
				{/* Anillo exterior */}
				<Box
					sx={{
						position: "absolute",
						inset: -6,
						borderRadius: "50%",
						border: "0.5px solid rgba(201, 168, 76, 0.08)",
					}}
				/>
				{/* Puntos cardinales */}
				{[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
					<Box
						key={deg}
						sx={{
							position: "absolute",
							width: deg % 90 === 0 ? 3.5 : 2,
							height: deg % 90 === 0 ? 3.5 : 2,
							borderRadius: "50%",
							backgroundColor: `rgba(201,168,76,${
								deg % 90 === 0 ? 0.25 : 0.12
							})`,
							top: "50%",
							left: "50%",
							transform: `rotate(${deg}deg) translateY(-${
								deg % 90 === 0 ? 55 : 53
							}px) translate(-50%, -50%)`,
						}}
					/>
				))}
				<Box
					component="img"
					src={WorldIcon}
					alt=""
					sx={{
						width: { xs: 80, sm: 80 },
						height: { xs: 80, sm: 80 },
						objectFit: "contain",
						filter: "drop-shadow(0 2px 15px rgba(201, 168, 76, 0.2))",
					}}
				/>
			</Box>

			{/* ── Título PASAPORTE ── */}
			<Typography
				sx={{
					fontSize: { xs: "1.6rem", sm: "2rem" },
					color: "#c9a84c",
					fontWeight: 700,
					letterSpacing: "0.18em",
					textTransform: "uppercase",
					textAlign: "center",
					lineHeight: 1,
					mb: 0.5,
					/* Efecto dorado estampado en relieve sobre piel */
					textShadow: `
						0 1px 0 rgba(201,168,76,0.3),
						0 2px 4px rgba(0,0,0,0.3),
						0 0 20px rgba(201,168,76,0.08)
					`,
				}}
			>
				PASAPORTE
			</Typography>

			{/* Subtítulos multilingüe */}
			<Typography
				sx={{
					fontSize: { xs: "0.45rem", sm: "0.5rem" },
					color: "rgba(201, 168, 76, 0.78)",
					textTransform: "uppercase",
					letterSpacing: "0.2em",
					mb: 2.5,
					textShadow: "0 1px 2px rgba(0,0,0,0.4), 0 0 6px rgba(0,0,0,0.2)",
				}}
			>
				Passport · パスポート · Passeport
			</Typography>

			{/* Línea divisoria doble */}
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					gap: "3px",
					mb: 2,
				}}
			>
				<Box
					sx={{
						width: 50,
						height: "1px",
						background:
							"linear-gradient(90deg, transparent, rgba(201,168,76,0.25), transparent)",
					}}
				/>
				<Box
					sx={{
						width: 35,
						height: "1px",
						background:
							"linear-gradient(90deg, transparent, rgba(201,168,76,0.15), transparent)",
					}}
				/>
			</Box>

			{/* ── Titular ── */}
			<Box sx={{ textAlign: "center", mb: 2 }}>
				<Typography
					sx={{
						fontSize: "0.45rem",
						color: "rgba(201, 168, 76, 0.8)",
						textTransform: "uppercase",
						letterSpacing: "0.25em",
						mb: 0.5,
						textShadow: "0 1px 2px rgba(0,0,0,0.4), 0 0 6px rgba(0,0,0,0.2)",
					}}
				>
					Titular / Holder
				</Typography>
				<Typography
					sx={{
						fontSize: { xs: "1rem", sm: "1.15rem" },
						color: "rgba(201, 168, 76, 0.95)",
						fontWeight: 600,
						letterSpacing: "0.05em",
						textShadow: "0 1px 3px rgba(0,0,0,0.4), 0 0 10px rgba(0,0,0,0.15)",
					}}
				>
					{userName}
				</Typography>
			</Box>

			{/* ── Contador de sellos ── */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1,
					px: 2.5,
					py: 0.6,
					borderRadius: "20px",
					border: "1px solid rgba(201, 168, 76, 0.12)",
					backgroundColor: "rgba(201, 168, 76, 0.03)",
					boxShadow: "inset 0 0 8px rgba(0,0,0,0.1)",
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", lineHeight: 0 }}>
					<LuPlane size={20} color="rgba(201, 168, 76, 0.85)" />
				</Box>
				<Typography
					sx={{
						fontSize: "0.6rem",
						color: "rgba(201, 168, 76, 0.88)",
						letterSpacing: "0.03em",
						fontWeight: 500,
						textShadow: "0 1px 2px rgba(0,0,0,0.35)",
					}}
				>
					{countriesCount === 0
						? "Sin sellos aún"
						: countriesCount === 1
						? "1 sello"
						: `${countriesCount} sellos`}
				</Typography>
			</Box>

			{/* ── Indicación inferior ── */}
			<Box
				sx={{
					position: "absolute",
					bottom: 24,
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					gap: 0.5,
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 0.75,
					}}
				>
					<Box
						sx={{
							width: 25,
							height: "1px",
							background:
								"linear-gradient(90deg, transparent, rgba(201,168,76,0.18))",
						}}
					/>
					<Box
						sx={{
							width: 3,
							height: 3,
							transform: "rotate(45deg)",
							backgroundColor: "rgba(201,168,76,0.15)",
						}}
					/>
					<Box
						sx={{
							width: 25,
							height: "1px",
							background:
								"linear-gradient(90deg, rgba(201,168,76,0.18), transparent)",
						}}
					/>
				</Box>
				<Typography
					sx={{
						fontSize: "0.4rem",
						color: "rgba(201, 168, 76, 0.7)",
						letterSpacing: "0.12em",
						textTransform: "uppercase",
						textShadow: "0 1px 2px rgba(0,0,0,0.4)",
					}}
				>
					Desliza para explorar →
				</Typography>
			</Box>
		</Box>
	);
};

export default PassportCover;
